/**
 * Google Tasks API Service
 * Sincroniza tarefas do Bigwa com Google Tasks
 * v2.0 - Metadados ocultos (30 quebras de linha)
 */

import { getTokenOrRefresh } from './googleAuth';
import { logger } from '../utils/logger';

const TASKS_API_BASE = 'https://tasks.googleapis.com/tasks/v1';
const BIGWA_LIST_TITLE = 'Bigwa';

// Metadados Bigwa codificados nas notas (bem escondidos)
const BIGWA_META_PREFIX = '<!-- BIGWA:';
const BIGWA_META_SUFFIX = ' -->';
const NEWLINES_BEFORE_META = '\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n'; // 30 quebras

interface GoogleTask {
  id?: string;
  title: string;
  notes?: string;
  due?: string; // RFC 3339 format
  status: 'needsAction' | 'completed';
  completed?: string; // RFC 3339 date when task was completed
  parent?: string;
  position?: string;
}

interface GoogleTaskList {
  id: string;
  title: string;
}

interface BigwaMetadata {
  projectId: string;
  referenceId?: string;
  bigwaTaskId: string;
}

interface GoogleTaskListResponse {
  items?: GoogleTaskList[];
}

interface GoogleTasksResponse {
  items?: GoogleTask[];
}

class GoogleTasksService {
  private bigwaListId: string | null = null;

  /**
   * Faz requisição autenticada à API do Google Tasks
   */
  private async fetch(endpoint: string, options: RequestInit = {}): Promise<unknown> {
    const token = await getTokenOrRefresh();

    const response = await fetch(`${TASKS_API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      logger.error('GoogleTasksService', 'API Error:', error);
      throw new Error(error.error?.message || `API Error: ${response.status}`);
    }

    // DELETE retorna 204 No Content
    if (response.status === 204) {
      return null;
    }

    return response.json();
  }

  /**
   * Obtém ou cria a lista "Bigwa" no Google Tasks
   */
  async getOrCreateBigwaList(): Promise<string> {
    if (this.bigwaListId) {
      return this.bigwaListId!;
    }

    // Buscar listas existentes
    const listsResponse = await this.fetch('/users/@me/lists') as GoogleTaskListResponse;
    const lists = listsResponse.items || [];

    // Procurar lista Bigwa
    const bigwaList = lists.find(l => l.title === BIGWA_LIST_TITLE);
    if (bigwaList) {
      this.bigwaListId = bigwaList.id;
      return this.bigwaListId!;
    }

    // Criar lista Bigwa
    const newList = await this.fetch('/users/@me/lists', {
      method: 'POST',
      body: JSON.stringify({ title: BIGWA_LIST_TITLE }),
    });

    this.bigwaListId = (newList as GoogleTaskList).id;
    return this.bigwaListId!;
  }

  /**
   * Codifica metadados Bigwa nas notas da tarefa
   * Usa 30 quebras de linha para esconder do preview
   */
  private encodeMetadata(notes: string = '', metadata: BigwaMetadata): string {
    const metaJson = JSON.stringify(metadata);
    const metaString = `${BIGWA_META_PREFIX}${metaJson}${BIGWA_META_SUFFIX}`;
    
    if (notes && notes.trim()) {
      return `${notes}${NEWLINES_BEFORE_META}${metaString}`;
    }
    return `${NEWLINES_BEFORE_META}${metaString}`;
  }

  /**
   * Decodifica metadados Bigwa das notas
   */
  private decodeMetadata(notes: string = ''): { metadata: BigwaMetadata | null; cleanNotes: string } {
    const metaRegex = /\n*<!-- BIGWA:(.+?) -->/;
    const match = notes.match(metaRegex);

    if (!match) {
      return { metadata: null, cleanNotes: notes };
    }

    try {
      const metadata = JSON.parse(match[1]) as BigwaMetadata;
      const cleanNotes = notes.replace(metaRegex, '').trim();
      return { metadata, cleanNotes };
    } catch {
      return { metadata: null, cleanNotes: notes };
    }
  }

  /**
   * Cria uma tarefa no Google Tasks
   */
  async createTask(task: {
    title: string;
    notes?: string;
    dueDate?: string;
    projectId: string;
    referenceId?: string;
    bigwaTaskId: string;
  }): Promise<string> {
    const listId = await this.getOrCreateBigwaList();

    const metadata: BigwaMetadata = {
      projectId: task.projectId,
      referenceId: task.referenceId,
      bigwaTaskId: task.bigwaTaskId,
    };

    const googleTask: GoogleTask = {
      title: task.title,
      notes: this.encodeMetadata(task.notes, metadata),
      status: 'needsAction',
    };

    // Converter data para RFC 3339
    if (task.dueDate) {
      googleTask.due = `${task.dueDate}T00:00:00.000Z`;
    }

    const created = await this.fetch(`/lists/${listId}/tasks`, {
      method: 'POST',
      body: JSON.stringify(googleTask),
    });

    return (created as GoogleTask).id!;
  }

  /**
   * Atualiza uma tarefa no Google Tasks
   */
  async updateTask(googleTaskId: string, updates: {
    title?: string;
    notes?: string;
    dueDate?: string | null;
    status?: 'needsAction' | 'completed';
    projectId?: string;
    referenceId?: string;
    bigwaTaskId?: string;
  }): Promise<void> {
    const listId = await this.getOrCreateBigwaList();

    // Buscar tarefa atual para preservar metadados
    const currentTask = await this.fetch(`/lists/${listId}/tasks/${googleTaskId}`) as GoogleTask;
    const { metadata: currentMeta, cleanNotes: currentCleanNotes } = this.decodeMetadata(currentTask.notes);

    const updateBody: Record<string, unknown> = {};

    if (updates.title !== undefined) {
      updateBody.title = updates.title;
    }

    if (updates.status !== undefined) {
      updateBody.status = updates.status;
    }

    if (updates.dueDate !== undefined) {
      updateBody.due = updates.dueDate ? `${updates.dueDate}T00:00:00.000Z` : null;
    }

    // Atualizar notas preservando metadados
    if (updates.notes !== undefined || updates.projectId !== undefined || updates.referenceId !== undefined) {
      const newMeta: BigwaMetadata = {
        projectId: updates.projectId ?? currentMeta?.projectId ?? '',
        referenceId: updates.referenceId ?? currentMeta?.referenceId,
        bigwaTaskId: updates.bigwaTaskId ?? currentMeta?.bigwaTaskId ?? '',
      };
      const newNotes = updates.notes ?? currentCleanNotes;
      updateBody.notes = this.encodeMetadata(newNotes, newMeta);
    }

    await this.fetch(`/lists/${listId}/tasks/${googleTaskId}`, {
      method: 'PATCH',
      body: JSON.stringify(updateBody),
    });

  }

  /**
   * Deleta uma tarefa do Google Tasks
   */
  async deleteTask(googleTaskId: string): Promise<void> {
    const listId = await this.getOrCreateBigwaList();

    await this.fetch(`/lists/${listId}/tasks/${googleTaskId}`, {
      method: 'DELETE',
    });

  }

  /**
   * Busca tarefas pendentes da lista Bigwa
   */
  async getPendingTasks(): Promise<Array<{
    googleTaskId: string;
    title: string;
    notes: string;
    dueDate?: string;
    status: 'needsAction' | 'completed';
    metadata: BigwaMetadata | null;
  }>> {
    const listId = await this.getOrCreateBigwaList();

    const response = await this.fetch(`/lists/${listId}/tasks?showCompleted=false&showHidden=false&maxResults=100`) as GoogleTasksResponse;
    const tasks = response.items || [];

    return tasks.map(task => {
      const { metadata, cleanNotes } = this.decodeMetadata(task.notes);
      return {
        googleTaskId: task.id!,
        title: task.title,
        notes: cleanNotes,
        dueDate: task.due ? task.due.split('T')[0] : undefined,
        status: task.status,
        metadata,
      };
    });
  }

  /**
   * Busca tarefas concluídas da lista Bigwa (para seção "Concluídos")
   * @param completedMin - ISO date string; only return tasks completed after this date
   */
  async getCompletedTasks(completedMin?: string): Promise<Array<{
    googleTaskId: string;
    title: string;
    notes: string;
    dueDate?: string;
    completedDate?: string;
    status: 'needsAction' | 'completed';
    metadata: BigwaMetadata | null;
  }>> {
    const listId = await this.getOrCreateBigwaList();

    let url = `/lists/${listId}/tasks?showCompleted=true&showHidden=true&maxResults=100`;
    if (completedMin) {
      url += `&completedMin=${completedMin}`;
    }
    const response = await this.fetch(url) as GoogleTasksResponse;
    const tasks = response.items || [];

    // Filter to only completed tasks
    return tasks
      .filter(task => task.status === 'completed')
      .map(task => {
        const { metadata, cleanNotes } = this.decodeMetadata(task.notes);
        return {
          googleTaskId: task.id!,
          title: task.title,
          notes: cleanNotes,
          dueDate: task.due ? task.due.split('T')[0] : undefined,
          completedDate: task.completed || undefined,
          status: task.status,
          metadata,
        };
      });
  }

  /**
   * Deleta múltiplas tarefas sequencialmente (tolerante a falhas individuais)
   * @returns Número de tarefas deletadas com sucesso
   */
  async deleteMultipleTasks(ids: string[]): Promise<number> {
    let deleted = 0;
    for (const id of ids) {
      try {
        await this.deleteTask(id);
        deleted++;
      } catch (err) {
        logger.warn('GoogleTasks', `Failed to delete task ${id}:`, err);
      }
    }
    return deleted;
  }

  /**
   * Busca todas as tarefas da lista Bigwa (pendentes + concluídas)
   */
  async getAllTasks(): Promise<Array<{
    googleTaskId: string;
    title: string;
    notes: string;
    dueDate?: string;
    status: 'needsAction' | 'completed';
    metadata: BigwaMetadata | null;
  }>> {
    const listId = await this.getOrCreateBigwaList();

    const response = await this.fetch(`/lists/${listId}/tasks?showCompleted=true&showHidden=true&maxResults=100`) as GoogleTasksResponse;
    const tasks = response.items || [];

    return tasks.map(task => {
      const { metadata, cleanNotes } = this.decodeMetadata(task.notes);
      return {
        googleTaskId: task.id!,
        title: task.title,
        notes: cleanNotes,
        dueDate: task.due ? task.due.split('T')[0] : undefined,
        status: task.status,
        metadata,
      };
    });
  }

  /**
   * Marca tarefa como completa
   */
  async completeTask(googleTaskId: string): Promise<void> {
    await this.updateTask(googleTaskId, { status: 'completed' });
  }

  /**
   * Marca tarefa como pendente
   */
  async uncompleteTask(googleTaskId: string): Promise<void> {
    await this.updateTask(googleTaskId, { status: 'needsAction' });
  }

  /**
   * Verifica se a API está acessível
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.fetch('/users/@me/lists');
      return true;
    } catch (error) {
      logger.error('GoogleTasksService', 'Teste de conexão falhou:', error);
      return false;
    }
  }

  /**
   * Limpa cache do listId
   */
  clearCache(): void {
    this.bigwaListId = null;
  }
}

export const googleTasksService = new GoogleTasksService();
